import { useMemo, useState } from 'react';
import {
    Grid,
    Typography,
    Button,
    Menu,
    MenuItem,
    Icon,
    Stack,
    Box,
    Divider,
    Dialog,
} from '../../../base';
import moment from 'moment';
import {
    IJobApplication,
    IServerJobApplicationTrail,
    IScheduleInterviewData,
} from '../../../common';
import { JobApplicationTrailActionTypes } from '../../../common/consts/jobApplication.consts';
import { CardWrapper, NextActionDetails } from './CandidateStatus.styles';
import { useTranslation } from 'react-i18next';
import {
    candidateStatusMenuOptions,
    CandidateStatusMenuKeys,
    CandidateStatusMenuKey,
    StepLifecycleStatuses,
    StepLifecycleStatus,
    lifecycleStatusConfig,
} from './CandidateStatus.consts';
import ScheduleInterview from './ScheduleInterview';
import { SendEmailBox } from '../EmailTab/EmailModals';
import PargodaModal from '../../PargodaModal';
import { useAppDispatch, useAppSelector } from '../../../base';
import {
    jobApplicationActions,
    jobApplicationSelectors,
} from '../../../actions';

interface CandidateStatusProps {
    jobApplication: IJobApplication;
    isLegacy?: boolean;
}

const CandidateStatus = (props: CandidateStatusProps) => {
    const { status, jobApplication, isLegacy = false } = props;
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const isScheduleInterviewLoading = useAppSelector(
        jobApplicationSelectors.getIsScheduleInterviewLoading
    );
    const scheduleInterviewResult = useAppSelector(
        jobApplicationSelectors.getScheduleInterviewResult
    );
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedOption, setSelectedOption] = useState<string>(
        candidateStatusMenuOptions[0].key
    );
    const [activeMenuView, setActiveMenuView] = useState<string | null>(null);
    const [isSendEmailModalOpen, setIsSendEmailModalOpen] = useState(false);
    const isMenuOpen = Boolean(anchorEl);

    const currentStep = useMemo(() => {
        // currentStep is a step ID, not an index - find it by ID
        return jobApplication.steps?.find(
            (step) => step.id === jobApplication.currentStep
        ) ?? null;
    }, [jobApplication.steps, jobApplication.currentStep]);

    const lifecycleStatus = (currentStep?.lifecycle_status ??
        StepLifecycleStatuses.AWAITING_SCHEDULING_REQUEST) as StepLifecycleStatus;

    const config = lifecycleStatusConfig[lifecycleStatus];

    const lastLifecycleChange = useMemo(() => {
        if (!jobApplication.trail?.length) return null;

        const lifecycleTrails = jobApplication.trail.filter(
            (entry: IServerJobApplicationTrail) =>
                entry.action_type ===
                    JobApplicationTrailActionTypes.lifecycleChange &&
                entry.params?.new_status === lifecycleStatus &&
                entry.params?.step_id === currentStep?.id
        );

        if (!lifecycleTrails.length) return null;

        const latest = lifecycleTrails.reduce(
            (a: IServerJobApplicationTrail, b: IServerJobApplicationTrail) =>
                new Date(a.created_at) > new Date(b.created_at) ? a : b
        );

        return latest.params?.changed_at
            ? moment(latest.params.changed_at)
            : moment(latest.created_at);
    }, [jobApplication.trail, lifecycleStatus, currentStep?.id]);

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        setActiveMenuView(null);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setActiveMenuView(null);
    };

    const handleMenuItemClick = (action: CandidateStatusMenuKey) => {
        setSelectedOption(action);
        if (action === CandidateStatusMenuKeys.SCHEDULE_INTERVIEW) {
            dispatch(jobApplicationActions.resetScheduleInterview());
            setActiveMenuView(action);
        } else if (action === CandidateStatusMenuKeys.SEND_EMAIL) {
            setIsSendEmailModalOpen(true);
            handleMenuClose();
            setAnchorEl(null);
        } else {
            handleMenuClose();
        }
    };

    const handleCancelScheduleInterview = () => {
        setActiveMenuView(null);
    };

    const handleSubmitScheduleInterview = (data: IScheduleInterviewData) => {
        dispatch(
            jobApplicationActions.scheduleInterview({
                jobApplicationId: jobApplication.id,
                interviewData: data,
            })
        );
    };

    const getSelectedLabel = () => {
        const selected = candidateStatusMenuOptions.find(
            (option) => option.key === selectedOption
        );
        return selected
            ? t(selected.value)
            : t(candidateStatusMenuOptions[0].value);
    };

    const isScheduleInterviewOpen =
        activeMenuView === CandidateStatusMenuKeys.SCHEDULE_INTERVIEW;

    return (
        <>
            <CardWrapper>
                <Grid container spacing={1}>
                    <Grid
                        container
                        item
                        xs={12}
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Grid item>
                            <Typography variant="h6">
                                {t('jobApplicationPage.candidateStatus')}
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Button
                                onClick={handleMenuClick}
                                variant="outlined"
                                endIcon={<Icon name="ExpandMoreOutlinedIcon" />}
                                color="secondary"
                                disabled={isLegacy}
                                sx={{
                                    minWidth: 190,
                                    justifyContent: 'space-between',
                                }}
                            >
                                {getSelectedLabel()}
                            </Button>
                            <Menu
                                anchorEl={anchorEl}
                                open={isMenuOpen}
                                onClose={handleMenuClose}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'left',
                                }}
                            >
                                {candidateStatusMenuOptions.map((option) => (
                                    <MenuItem
                                        key={option.key}
                                        disabled={option.disabled}
                                        onClick={() =>
                                            handleMenuItemClick(option.key)
                                        }
                                    >
                                        {t(option.value)}
                                    </MenuItem>
                                ))}
                            </Menu>
                            <Dialog
                                open={isScheduleInterviewOpen}
                                onClose={() => {}} // Prevent closing on backdrop click
                                maxWidth="sm"
                                disableEscapeKeyDown
                                PaperProps={{
                                    sx: {
                                        margin: 0,
                                        maxWidth: '540px',
                                        width: '100%',
                                    },
                                }}
                            >
                                <ScheduleInterview
                                    onCancel={handleCancelScheduleInterview}
                                    onSubmit={handleSubmitScheduleInterview}
                                    isLoading={isScheduleInterviewLoading}
                                    scheduleInterviewResult={
                                        scheduleInterviewResult
                                    }
                                />
                            </Dialog>
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <Stack spacing={3}>
                            <Typography
                                variant="subtitle1"
                                color="primary.main"
                            >
                                {t(config.statusText)}
                            </Typography>
                            <Box>
                                <Typography
                                    variant="body1"
                                    color="textSecondary"
                                >
                                    {t('jobApplicationPage.nextAction')}
                                </Typography>
                                <NextActionDetails>
                                    <Grid container alignItems="center">
                                        <Grid item>
                                            <Typography variant="body1">
                                                {t(config.nextAction)}
                                            </Typography>
                                        </Grid>
                                        {config.completedActionLabel &&
                                            lastLifecycleChange && (
                                                <>
                                                    <Divider
                                                        orientation="vertical"
                                                        flexItem
                                                        sx={{ marginX: 1 }}
                                                    />
                                                    <Grid item>
                                                        <Typography
                                                            variant="body2"
                                                            color="success.main"
                                                        >
                                                            {t(
                                                                config.completedActionLabel,
                                                                {
                                                                    time: lastLifecycleChange.format(
                                                                        'h:mm A MMMM D'
                                                                    ),
                                                                }
                                                            )}
                                                        </Typography>
                                                    </Grid>
                                                </>
                                            )}
                                    </Grid>
                                </NextActionDetails>
                            </Box>
                        </Stack>
                    </Grid>
                </Grid>
            </CardWrapper>
            <PargodaModal
                modalTitle={t('jobApplicationPage.sendEmail')}
                isModalOpen={isSendEmailModalOpen}
                setIsModalOpen={setIsSendEmailModalOpen}
                sx={{ width: '800px', maxWidth: '90%' }}
            >
                <SendEmailBox
                    jobApplication={jobApplication}
                    onSave={() => setIsSendEmailModalOpen(false)}
                />
            </PargodaModal>
        </>
    );
};

export default CandidateStatus;
